import { Link } from "@tanstack/react-router";
import { memo, useMemo, useState } from "react";
import type { Product } from "@/lib/products";
import { formatPrice } from "@/lib/cart";
import { SmartImage } from "@/components/site/SmartImage";
import { useWishlist } from "@/lib/wishlist";
import { Heart } from "lucide-react";
import { productImageWebpSrcSet, PRODUCT_CARD_SIZES } from "@/lib/image-srcset";

const MAX_DOTS = 5;

function ProductCardImpl({
  product,
  index = 0,
  eager = false,
}: {
  product: Product;
  index?: number;
  eager?: boolean;
}) {
  const variants = product.variants ?? [];
  const hasVariants = variants.length > 0;

  // Front-image priority: hovered variant image → selected variant image → product.image
  const [hoveredVariant, setHoveredVariant] = useState<string | null>(null);
  const defaultVariant = useMemo(
    () => variants.find((v) => v.is_default) ?? variants[0] ?? null,
    [variants],
  );
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    defaultVariant?.id ?? null,
  );

  const activeVariant =
    variants.find((v) => v.id === hoveredVariant) ??
    variants.find((v) => v.id === selectedVariantId) ??
    defaultVariant;

  const frontSrc = activeVariant?.image_url || product.image;

  // Back-view hover: explicit hover_image_url, else 2nd gallery image (typically the back).
  const fallbackHover = (product.gallery_images ?? []).find(
    (src) => src && src !== product.image && src !== frontSrc,
  );
  const hoverSrc = product.hover_image_url || fallbackHover || "";
  const hasHover = !!hoverSrc && hoverSrc !== frontSrc;

  const { isWished, toggle } = useWishlist();
  const wished = isWished(product.slug);

  return (
    <div className="group reveal" style={{ animationDelay: `${0.05 * index}s` }}>
      <Link
        to="/product/$slug"
        params={{ slug: product.slug }}
        preload="intent"
        className="block"
      >
        <div className="relative overflow-hidden bg-secondary aspect-[4/5] img-frame">
          <SmartImage
            src={frontSrc}
            alt={product.name}
            eager={eager}
            width={1024}
            height={1280}
            webpSrcSet={productImageWebpSrcSet(frontSrc)}
            sizes={PRODUCT_CARD_SIZES}
            className={`h-full w-full object-cover absolute inset-0 transition-opacity duration-700 ${
              hasHover ? "group-hover:opacity-0" : ""
            }`}
          />
          {hasHover && (
            <picture>
              {productImageWebpSrcSet(hoverSrc) && (
                <source
                  type="image/webp"
                  srcSet={productImageWebpSrcSet(hoverSrc)}
                  sizes={PRODUCT_CARD_SIZES}
                />
              )}
              <img
                src={hoverSrc}
                alt={`${product.name} — back view`}
                loading="lazy"
                decoding="async"
                width={1024}
                height={1280}
                className="h-full w-full object-cover absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 group-hover:scale-105 transform-gpu"
              />
            </picture>
          )}
          <button
            type="button"
            aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void toggle(product.slug);
            }}
            className="absolute bottom-3 right-3 z-10 h-9 w-9 inline-flex items-center justify-center bg-bone/85 backdrop-blur-sm border border-ink/10 hover:bg-bone transition-colors"
          >
            <Heart
              className={`h-4 w-4 transition-colors ${
                wished ? "fill-ink text-ink" : "text-ink/70"
              }`}
            />
          </button>
        </div>
        <div className="mt-6 text-center">
          <h3 className="font-serif text-lg md:text-xl leading-tight font-light text-ink">
            {product.name}
          </h3>
          <p className="text-[10px] uppercase tracking-[0.28em] text-stone mt-2">
            {product.category}
            {hasVariants && (
              <span className="ml-2">
                · {variants.length} {variants.length === 1 ? "colour" : "colours"}
              </span>
            )}
          </p>
          <span className="block mt-3 text-sm tabular-nums text-ink font-light">
            {formatPrice(product.price)}
          </span>
        </div>
      </Link>

      {hasVariants && (
        <div className="mt-4 flex items-center justify-center gap-2">
          {variants.slice(0, MAX_DOTS).map((v) => {
            const active = (selectedVariantId ?? defaultVariant?.id) === v.id;
            return (
              <button
                key={v.id}
                type="button"
                aria-label={`Show ${v.color_name}`}
                title={v.color_name}
                onMouseEnter={() => setHoveredVariant(v.id)}
                onMouseLeave={() => setHoveredVariant(null)}
                onFocus={() => setHoveredVariant(v.id)}
                onBlur={() => setHoveredVariant(null)}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setSelectedVariantId(v.id);
                }}
                style={{ backgroundColor: v.color_hex }}
                className={`h-3 w-3 rounded-full border transition-all ${
                  active
                    ? "border-ink ring-1 ring-ink ring-offset-2 ring-offset-background scale-110"
                    : "border-border hover:scale-110"
                }`}
              />
            );
          })}
          {variants.length > MAX_DOTS && (
            <span className="ml-1 text-[10px] text-stone tabular-nums">
              +{variants.length - MAX_DOTS}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

export const ProductCard = memo(ProductCardImpl);

